import {
  DesktopOutlined,
  EyeOutlined,
  GlobalOutlined,
  LoadingOutlined,
  MobileOutlined,
  ReloadOutlined,
  RiseOutlined,
  TeamOutlined
} from '@ant-design/icons';
import { Boundary } from 'components/common';
import { useDocumentTitle, useScrollTop } from 'hooks';
import PropType from 'prop-types';
import React, {
  useCallback, useEffect, useMemo, useState
} from 'react';
import firebase from 'services/firebase';
import {
  buildProfileAnalytics,
  formatAnalyticsDate
} from 'utils/profileAnalytics';

const RANGE_OPTIONS = [
  { label: 'Last 7 days', value: 7 },
  { label: 'Last 30 days', value: 30 },
  { label: 'Last 90 days', value: 90 },
  { label: 'All time', value: 0 }
];

const formatNumber = (value) => Number(value || 0).toLocaleString();

const AnalyticsMetric = ({
  icon: Icon, label, note, tone, value
}) => (
  <article className={`analytics-metric analytics-metric-${tone}`}>
    <span className="analytics-metric-icon"><Icon /></span>
    <div>
      <span>{label}</span>
      <strong>{formatNumber(value)}</strong>
      <small>{note}</small>
    </div>
  </article>
);

const Breakdown = ({ icon: Icon, items, title }) => (
  <section className="analytics-panel">
    <div className="analytics-panel-heading">
      <span className="analytics-panel-icon"><Icon /></span>
      <h3>{title}</h3>
    </div>
    <div className="analytics-breakdown">
      {items.map((item) => (
        <div className="analytics-breakdown-item" key={item.label}>
          <div>
            <strong>{item.label}</strong>
            <span>
              {formatNumber(item.count)}
              {' views · '}
              {item.share}
              %
            </span>
          </div>
          <div className="analytics-progress">
            <span style={{ width: `${item.share}%` }} />
          </div>
        </div>
      ))}
      {!items.length ? <p className="text-subtle">No view data yet.</p> : null}
    </div>
  </section>
);

AnalyticsMetric.propTypes = {
  icon: PropType.elementType.isRequired,
  label: PropType.string.isRequired,
  note: PropType.string.isRequired,
  tone: PropType.string.isRequired,
  value: PropType.number.isRequired
};

Breakdown.propTypes = {
  icon: PropType.elementType.isRequired,
  items: PropType.arrayOf(PropType.shape({
    count: PropType.number.isRequired,
    label: PropType.string.isRequired,
    share: PropType.number.isRequired
  })).isRequired,
  title: PropType.string.isRequired
};

const Analytics = () => {
  useDocumentTitle('Profile Analytics | TaKKa Admin');
  useScrollTop();

  const [views, setViews] = useState([]);
  const [users, setUsers] = useState([]);
  const [aliasesByUser, setAliasesByUser] = useState({});
  const [rangeDays, setRangeDays] = useState(30);
  const [isLoading, setLoading] = useState(true);
  const [message, setMessage] = useState('');

  const loadAnalytics = useCallback(async () => {
    setLoading(true);
    setMessage('');
    try {
      const [viewsSnapshot, usersSnapshot, aliasesSnapshot] = await Promise.all([
        firebase.getProfileViews(),
        firebase.getAllUsers(),
        firebase.getAllReadyProfiles()
      ]);
      const loadedViews = viewsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      }));
      const loadedUsers = usersSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      }));
      const loadedAliases = {};

      aliasesSnapshot.docs.forEach((doc) => {
        const userId = doc.data().user;
        if (!userId) return;
        loadedAliases[userId] = [...(loadedAliases[userId] || []), doc.id];
      });

      setViews(loadedViews);
      setUsers(loadedUsers);
      setAliasesByUser(loadedAliases);
    } catch (error) {
      setMessage(error.message || 'Failed to load profile analytics.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  const analytics = useMemo(() => buildProfileAnalytics({
    aliasesByUser,
    rangeDays,
    users,
    views
  }), [aliasesByUser, rangeDays, users, views]);
  const trendMaximum = Math.max(
    ...analytics.dailyTrend.map((item) => item.views),
    1
  );
  const selectedRange = RANGE_OPTIONS.find((option) => option.value === rangeDays);

  return (
    <Boundary>
      <div className="analytics-page">
        <header className="analytics-header">
          <div>
            <span className="analytics-kicker">PROFILE PERFORMANCE</span>
            <h1>Views overview</h1>
            <p>Track profile traffic, top users, visitor sources, and device activity.</p>
          </div>
          <div className="analytics-header-actions">
            <select
              aria-label="Analytics date range"
              className="input-form"
              onChange={(event) => setRangeDays(Number(event.target.value))}
              value={rangeDays}
            >
              {RANGE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            <button
              className="button button-small"
              disabled={isLoading}
              onClick={loadAnalytics}
              type="button"
            >
              {isLoading ? <LoadingOutlined /> : <ReloadOutlined />}
              {' Refresh'}
            </button>
          </div>
        </header>

        {message ? <p className="toast-error">{message}</p> : null}

        {isLoading ? (
          <div className="analytics-loading">
            <LoadingOutlined />
            <span>Loading profile analytics...</span>
          </div>
        ) : (
          <>
            <section className="analytics-metrics">
              <AnalyticsMetric
                icon={EyeOutlined}
                label="All-time views"
                note="Since analytics tracking started"
                tone="blue"
                value={analytics.allTimeViews}
              />
              <AnalyticsMetric
                icon={RiseOutlined}
                label={`${selectedRange.label} views`}
                note={`${analytics.todayViews.toLocaleString()} views today`}
                tone="green"
                value={analytics.rangeViews}
              />
              <AnalyticsMetric
                icon={TeamOutlined}
                label="Unique visitors"
                note="Anonymous browser identifiers"
                tone="violet"
                value={analytics.uniqueVisitors}
              />
              <AnalyticsMetric
                icon={GlobalOutlined}
                label="Profiles viewed"
                note="Different user accounts reached"
                tone="orange"
                value={analytics.viewedProfiles}
              />
            </section>

            <section className="analytics-panel analytics-trend-panel">
              <div className="analytics-panel-heading">
                <span className="analytics-panel-icon"><RiseOutlined /></span>
                <div>
                  <h3>Daily view trend</h3>
                  <p>Up to the most recent 30 days</p>
                </div>
              </div>
              <div className="analytics-chart" role="img" aria-label="Daily profile views chart">
                {analytics.dailyTrend.map((item) => (
                  <div className="analytics-chart-column" key={item.day}>
                    <span className="analytics-chart-value">{item.views}</span>
                    <div className="analytics-chart-track">
                      <span style={{
                        height: `${Math.max((item.views / trendMaximum) * 100, item.views ? 5 : 0)}%`
                      }}
                      />
                    </div>
                    <small>{item.day.slice(5)}</small>
                  </div>
                ))}
              </div>
            </section>

            <section className="analytics-two-column">
              <Breakdown
                icon={GlobalOutlined}
                items={analytics.sources}
                title="Traffic sources"
              />
              <Breakdown
                icon={analytics.devices[0] && analytics.devices[0].label === 'Mobile'
                  ? MobileOutlined
                  : DesktopOutlined}
                items={analytics.devices}
                title="Devices"
              />
            </section>

            <section className="analytics-panel">
              <div className="analytics-panel-heading analytics-table-heading">
                <div>
                  <h3>Top user profiles</h3>
                  <p>Ranked by views in the selected period.</p>
                </div>
                <strong>
                  {analytics.topUsers.length}
                  {' viewed profiles'}
                </strong>
              </div>
              <div className="analytics-table-wrap">
                <table className="analytics-table">
                  <thead>
                    <tr>
                      <th>Rank</th>
                      <th>User</th>
                      <th>Profile links</th>
                      <th>Views</th>
                      <th>Unique visitors</th>
                      <th>Share</th>
                      <th>Last view</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analytics.topUsers.map((user, index) => (
                      <tr key={user.userId}>
                        <td><span className="analytics-rank">{index + 1}</span></td>
                        <td>
                          <strong>{user.name}</strong>
                          <small>{user.email || user.userId}</small>
                        </td>
                        <td>
                          <div className="analytics-aliases">
                            {user.aliases.map((alias) => (
                              <a
                                href={`/#/user/${alias}`}
                                key={alias}
                                rel="noopener noreferrer"
                                target="_blank"
                              >
                                /
                                {alias}
                              </a>
                            ))}
                            {!user.aliases.length ? <span>—</span> : null}
                          </div>
                        </td>
                        <td><strong>{formatNumber(user.views)}</strong></td>
                        <td>{formatNumber(user.uniqueVisitors)}</td>
                        <td>
                          {user.share}
                          %
                        </td>
                        <td>{formatAnalyticsDate(user.lastViewTime)}</td>
                      </tr>
                    ))}
                    {!analytics.topUsers.length ? (
                      <tr>
                        <td className="text-subtle analytics-empty" colSpan="7">
                          No profile views have been recorded in this period.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}
      </div>
    </Boundary>
  );
};

export default Analytics;
